import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, Phone } from "lucide-react";
import { COMPANY } from "../lib/site";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/locations", label: "Locations" },
  { to: "/employers", label: "Employers" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const overHero = location.pathname === "/" && !scrolled;

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-background/70 backdrop-blur-xl border-b border-border" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1500px] px-6 md:px-10 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
          <img src="/logo-mark.png" alt="HR Direct Services" className="w-10 h-10 object-contain" />
          <span className={`font-display uppercase tracking-tight leading-[0.9] text-[15px] ${overHero ? "text-white" : ""}`}>
            HR Direct<br />
            <span className={`text-[11px] tracking-[0.2em] ${overHero ? "text-white/60" : "text-muted-foreground"}`}>STAFFING SERVICES</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `overline transition-colors hover:text-flame ${isActive ? "text-flame" : overHero ? "text-white" : "text-foreground"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={COMPANY.phoneHref}
            data-testid="nav-phone"
            className={`hidden md:inline-flex items-center gap-2 overline hover:text-flame transition-colors ${overHero ? "text-white" : ""}`}
          >
            <Phone size={14} className="text-flame" /> {COMPANY.phone}
          </a>
          <Link
            to="/apply"
            data-testid="nav-apply-cta"
            className="hidden sm:inline-flex items-center gap-2 bg-cobalt text-white px-5 py-2.5 rounded-full overline hover:bg-flame transition-colors duration-300"
          >
            Apply Now <ArrowUpRight size={14} />
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            className={`lg:hidden p-2 ${overHero ? "text-white" : ""}`}
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden bg-background border-b border-border"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <NavLink key={l.to} to={l.to} className="font-display uppercase text-2xl tracking-tight">
                  {l.label}
                </NavLink>
              ))}
              <Link to="/apply" className="mt-2 inline-flex items-center gap-2 bg-cobalt text-white px-5 py-3 rounded-full overline w-fit">
                Apply Now <ArrowUpRight size={14} />
              </Link>
              <a href={COMPANY.phoneHref} className="inline-flex items-center gap-2 overline text-foreground/80">
                <Phone size={14} className="text-flame" /> {COMPANY.phone}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
